import type {
    SketchSolverIslandSnapshot,
    SketchSolverRecord,
    SketchSolverRecordSource,
} from './SketchSolverRecordTypes';
import {
    areSketchSolverIslandSnapshotsEqual,
    isSketchSolverIslandSnapshot,
    isSketchSolverRecordConsistent,
    sortUniqueStrings,
} from './SolverIslandSnapshot';

/**
 * solver island 快照读取输入。
 */
export interface SolverIslandSnapshotReadInput {
    /** 草图稳定 ID。 */
    readonly sketchId: string;
    /** solver island 稳定 ID。 */
    readonly islandId: string;
    /** island 内实体 ID。 */
    readonly entityIds: readonly string[];
    /** island 内点 ID。 */
    readonly pointIds: readonly string[];
    /** island 内约束 ID。 */
    readonly constraintIds: readonly string[];
    /** island 内尺寸 ID。 */
    readonly dimensionIds: readonly string[];
}

/**
 * solver island 快照读取器。
 *
 * 设计说明：
 * Phase 2.0 只定义抽象端口。真实实现未来由草图求解器 adapter 提供，历史核心不 import solver、
 * Three.js 对象或 web-client 状态。
 */
export interface SolverIslandSnapshotReader {
    /**
     * 读取 solver island 当前快照。
     *
     * @param input 快照读取输入。
     * @returns solver island 快照。
     */
    readonly readSolverIslandSnapshot: (input: SolverIslandSnapshotReadInput) => SketchSolverIslandSnapshot;
}

/**
 * solver 记录创建输入。
 */
export interface CreateSketchSolverRecordInput {
    /** 记录稳定 ID。 */
    readonly recordId: string;
    /** 记录来源。 */
    readonly source: SketchSolverRecordSource;
    /** before 快照。 */
    readonly before: SketchSolverIslandSnapshot;
    /** after 快照。 */
    readonly after: SketchSolverIslandSnapshot;
    /** 是否由拖拽 commit 产生。 */
    readonly dragCommit?: boolean;
    /** 是否由自动关系产生。 */
    readonly automaticRelation?: boolean;
    /** 是否由自动 driven 尺寸产生。 */
    readonly automaticDrivenDimension?: boolean;
}

/**
 * solver recorder 捕获输入。
 */
export interface SolverStateRecorderCaptureInput extends SolverIslandSnapshotReadInput {
    /** 捕获阶段。 */
    readonly phase: 'before' | 'after';
}

/**
 * solver recorder。
 */
export interface SolverStateRecorder {
    /**
     * 捕获 before 快照。
     *
     * @param input 快照读取输入。
     * @returns before 快照。
     */
    readonly captureBefore: (input: SolverIslandSnapshotReadInput) => SketchSolverIslandSnapshot;
    /**
     * 捕获 after 快照。
     *
     * @param input 快照读取输入。
     * @returns after 快照。
     */
    readonly captureAfter: (input: SolverIslandSnapshotReadInput) => SketchSolverIslandSnapshot;
    /**
     * 创建 solver 记录。
     *
     * @param input 记录创建输入。
     * @returns solver 记录。
     */
    readonly createRecord: (input: CreateSketchSolverRecordInput) => SketchSolverRecord;
}

/**
 * 判断两个 ID 列表是否为相同集合。
 *
 * @param left 左侧 ID 列表。
 * @param right 右侧 ID 列表。
 * @returns 两个 ID 列表是否为相同集合。
 */
function areIdSetsEqual(
    left: readonly string[],
    right: readonly string[],
): boolean {
    return JSON.stringify(sortUniqueStrings(left)) === JSON.stringify(sortUniqueStrings(right));
}

/**
 * 校验快照属于指定 island。
 *
 * @param input 快照读取输入。
 * @param snapshot 快照。
 */
function assertSnapshotMatchesInput(
    input: SolverIslandSnapshotReadInput,
    snapshot: SketchSolverIslandSnapshot,
): void {
    if (snapshot.sketchId !== input.sketchId) {
        throw new Error(`solver island ${input.islandId} 的草图 ID 不一致。`);
    }

    if (snapshot.islandId !== input.islandId) {
        throw new Error(`solver island 快照 ID 不一致：${snapshot.islandId}`);
    }

    if (!areIdSetsEqual(snapshot.entityIds, input.entityIds)) {
        throw new Error(`solver island ${input.islandId} 的实体边界不一致。`);
    }

    if (!areIdSetsEqual(snapshot.pointIds, input.pointIds)) {
        throw new Error(`solver island ${input.islandId} 的点边界不一致。`);
    }

    if (!areIdSetsEqual(snapshot.constraintIds, input.constraintIds)) {
        throw new Error(`solver island ${input.islandId} 的约束边界不一致。`);
    }

    if (!areIdSetsEqual(snapshot.dimensionIds, input.dimensionIds)) {
        throw new Error(`solver island ${input.islandId} 的尺寸边界不一致。`);
    }
}

/**
 * 捕获并校验 solver island 快照。
 *
 * @param reader 快照读取器。
 * @param input 快照读取输入。
 * @returns solver island 快照。
 */
function captureSolverIslandSnapshot(
    reader: SolverIslandSnapshotReader,
    input: SolverIslandSnapshotReadInput,
): SketchSolverIslandSnapshot {
    const snapshot = reader.readSolverIslandSnapshot(input);

    if (!isSketchSolverIslandSnapshot(snapshot)) {
        throw new Error(`solver island ${input.islandId} 快照不是可持久化主状态。`);
    }

    assertSnapshotMatchesInput(input, snapshot);

    return snapshot;
}

/**
 * 创建 solver 记录。
 *
 * @param input 记录创建输入。
 * @returns solver 记录。
 * @throws 当 before/after island 边界不一致或两份快照没有变化时抛出错误。
 */
export function createSketchSolverRecord(input: CreateSketchSolverRecordInput): SketchSolverRecord {
    const record: SketchSolverRecord = {
        recordId: input.recordId,
        sketchId: input.before.sketchId,
        islandId: input.before.islandId,
        source: input.source,
        before: input.before,
        after: input.after,
        dragCommit: input.dragCommit,
        automaticRelation: input.automaticRelation,
        automaticDrivenDimension: input.automaticDrivenDimension,
    };

    if (!isSketchSolverRecordConsistent(record)) {
        throw new Error(`solver 记录 ${input.recordId} 的 before / after island 边界不一致。`);
    }

    if (areSketchSolverIslandSnapshotsEqual(input.before, input.after)) {
        throw new Error(`solver 记录 ${input.recordId} 的 before / after 没有变化。`);
    }

    return record;
}

/**
 * 创建 solver state recorder。
 *
 * @param reader solver island 快照读取器。
 * @returns solver state recorder。
 */
export function createSolverStateRecorder(reader: SolverIslandSnapshotReader): SolverStateRecorder {
    return {
        captureBefore(input) {
            // before 捕获必须发生在 command 改写约束或几何之前，用于避免重求解带来的多解漂移。
            return captureSolverIslandSnapshot(reader, input);
        },
        captureAfter(input) {
            // after 捕获必须发生在 solver 完成并写回主状态之后，记录用户看到的最终解。
            return captureSolverIslandSnapshot(reader, input);
        },
        createRecord(input) {
            return createSketchSolverRecord(input);
        },
    };
}
